import { Box, Button, Divider, Flex, Heading, Text } from "@chakra-ui/react";
import { RiQuestionnaireFill } from "react-icons/ri";
import { IoIosCloseCircleOutline, IoMdBulb, IoMdCheckmarkCircleOutline } from "react-icons/io";
import AudioSample from "./audio-sample";
import { useContext, useState } from "react";
import NextPrevButtons from "../next-prev-buttons";
import { quizColors } from "../../hooks/use-guide";
import { LocalizationContext } from "../../hooks/use-localization";
import { QuizStep } from "../../services/guides/types";

interface Props {
  step: QuizStep;
  voice?: SpeechSynthesisVoice;
  onNextClick: () => void;
  onPrevClick: () => void;
}

export default function ViewStep(props: Props) {
  const { l } = useContext(LocalizationContext);
  const { content, question, examples, options, correct_answer_index, explanation } = props.step;

  const [answerStatus, setAnswerStatus] = useState<"correct" | "incorrect" | "pending">("pending");

  const onAnswerClick = (index: number) => {
    setAnswerStatus(index === correct_answer_index ? "correct" : "incorrect");
  };

  const onNextClick = () => {
    setAnswerStatus("pending");
    props.onNextClick();
  };

  const onPrevClick = () => {
    setAnswerStatus("pending");
    props.onPrevClick();
  };

  return (
    <Flex flexDir="column">
      <Flex
        flexDir="column"
        borderTopRadius={5}
        p={{ base: 5, md: 12 }}
        pt={10}
        backgroundColor={quizColors.primary}
        position="relative"
        justifyContent="center"
        alignItems={"center"}
        color="white"
      >
        <Flex alignItems={"center"} opacity={0.3}>
          <IoMdBulb color="white" fontSize={"25px"} />
        </Flex>

        <Text id="step-content" mt={5} fontSize={{ md: "md" }}>
          {content}
        </Text>
        {!!examples?.length && (
          <Box w="100%" mt={5}>
            <Text fontSize="sm" fontWeight={"bold"} mb={2}>
              {l ? l["guide.step.examples"] : "Examples"}
            </Text>
            {examples.map((e, i) => (
              <AudioSample id={`audio-sample-${i}`} key={i} example={e} voice={props.voice} />
            ))}
          </Box>
        )}
      </Flex>
      <Flex height={0} zIndex={1} alignItems="center" justifyContent={"center"}>
        <Box
          w="20px"
          height="20px"
          transform="rotate(45deg)"
          backgroundColor={quizColors.primary}
        ></Box>
      </Flex>

      <Flex
        position={"relative"}
        borderBottomRadius={5}
        p={12}
        pt={10}
        mb={5}
        backgroundColor={quizColors.secondary}
        justifyContent="center"
        alignItems={"center"}
        flexDir={"column"}
      >
        {answerStatus === "pending" ? (
          <>
            <Flex alignItems={"center"} opacity={0.3}>
              <RiQuestionnaireFill color="white" fontSize={"25px"} />
            </Flex>

            <Text id="step-question" mt={5} fontSize="md" color="white">
              {question}
            </Text>

            <Flex flexWrap={"wrap"} justifyContent="space-around" gap={"10px"} w="100%" mt={7}>
              {options.map((text, i) => (
                <Button
                  id={`question-answer-${i}`}
                  whiteSpace="initial"
                  minH={10}
                  h="auto"
                  variant="outline"
                  backgroundColor="transparent"
                  color="white"
                  _hover={{ backgroundColor: "rgba(255,255,255,0.2)" }}
                  key={i}
                  onClick={() => onAnswerClick(i)}
                  w={{ base: "100%", lg: "calc(50% - 10px)" }}
                >
                  {text}
                </Button>
              ))}
            </Flex>
          </>
        ) : (
          <Flex color="white" flexDir="column" justifyContent="center" alignItems={"center"}>
            <Flex lineHeight={1}>
              {answerStatus === "correct" ? (
                <>
                  <IoMdCheckmarkCircleOutline id="correct-icon" fontSize="40px" color={"white"} />
                </>
              ) : (
                <>
                  <IoIosCloseCircleOutline id="incorrect-icon" fontSize="40px" />
                </>
              )}
            </Flex>

            <Text fontSize="sm" fontStyle={"italic"} mt={7} mb={7}>
              {explanation}
            </Text>

            {answerStatus === "incorrect" && (
              <Box
                id="retry-link"
                fontSize="xs"
                textDecor={"underline"}
                onClick={() => setAnswerStatus("pending")}
                opacity="0.5"
                cursor="pointer"
              >
                {l ? l["guide.retry"] : "Retry"}
              </Box>
            )}
          </Flex>
        )}
      </Flex>

      <NextPrevButtons
        showPrev
        showNext={answerStatus !== "pending"}
        onNextClick={onNextClick}
        onPrevClick={onPrevClick}
      />
    </Flex>
  );
}
